import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DoCheck,
    DOCUMENT,
    ElementRef,
    EmbeddedViewRef,
    Inject,
    Injector,
    Input,
    NgZone,
    Renderer2,
    TemplateRef,
    Type,
    ViewChild,
    ViewContainerRef
} from '@angular/core';
import * as _ from 'lodash-es';
import {
    animationFrameScheduler,
    BehaviorSubject,
    combineLatest,
    defer,
    EMPTY,
    fromEvent,
    merge,
    NEVER,
    Observable,
    of,
    Subject,
    timer
} from 'rxjs';
import {
    audit,
    auditTime,
    distinctUntilChanged,
    filter,
    finalize,
    map,
    mapTo,
    repeatWhen,
    share,
    startWith,
    switchMap,
    switchMapTo,
    take,
    takeUntil,
    tap
} from 'rxjs/operators';
import {dependOn} from '../../../../../common/rxjs/depend-on';
import {fromEventOutsideAngularZone} from '../../../../../common/rxjs/from-event-outside-angular-zone';
import {destroy$} from '../../../../destroyable';
import {ElementRulerDirective} from '../../../../element-ruler';
import {WIDGET_UPDATES_GATING$} from '../../../../layout/widget/widget-updates-gating';
import {TableColumn, TableColumnConfig, TableColumnsSizingMode} from '../../table';
import {TableColumnTemplateContext} from '../../table-column-template-context';
import {TableColumnDirective} from '../../table-column.directive';
import {TableComponent} from '../../table.component';
import {TablePluginColumnConfigAddedArgs, TablePluginColumnsSetupArgs} from '../table-plugin';
import {ColumnMeta} from './column-meta';
import {ColumnsAutoResizer} from './columns-auto-resizer';
import {Pr, Px, TableColumnWidthUnit} from './table-column-width-units';
import {TableColumnsResizerDisabledHTMLElement} from './table-columns-resizer-disabled.directive';

type Point = [number, number];
const Y = 1; // Index of the Y coordinate/size in a Point array.

const SPLITTER_AREA_THICKNESS = 15; // Should the same as the thickness constant in the styles (splitterAreaThickness).
const SPLITTER_AREA_HALF_THICKNESS = Math.trunc(SPLITTER_AREA_THICKNESS / 2);
const SPLITTER_APPEARANCE_DELAY = 300;
const SPLITTER_TOO_LARGE_SHIFT_DELTA = SPLITTER_AREA_THICKNESS * 2;

interface ColumnSplitter {
    index: number;
    leftColumnMeta: ColumnMeta;
    rightColumnMeta: ColumnMeta;
    top?: number;
    left?: number;
    height?: number;
    areaTop?: number;
    areaLeft?: number;
}

interface ColumnWidthCalculatorData {
    tableWidth: number;
    availableTableWidth: number;
    columnsMeta: ColumnMeta[];
}

interface SplitterDisplayInfo {
    splitter?: ColumnSplitter;
    splitterX?: number;
    signY?: number;
}

interface SplitterMovingInfo {
    splitter: ColumnSplitter;
    shift: number;
    shiftingRange: Point;
    isTooLargeShiftDelta: boolean;
}

const RESIZING_CLASS = '-resizing';
const RESIZING_TOO_LARGE_SHIFT_DELTA_CLASS = '-shift-out-of-range';

const DEFAULT_TABLE_COLUMN_SIZING_MODE: TableColumnsSizingMode = TableColumnsSizingMode.MANUAL;

@Component({
    selector: 'table-column-resizer',
    templateUrl: './table-columns-resizer.component.html',
    styleUrls: ['./table-columns-resizer.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TableColumnsResizerComponent implements DoCheck, AfterViewInit {

    private static isElementDisablesResizer(element: Element): boolean {
        let matchedElement: TableColumnsResizerDisabledHTMLElement = element as TableColumnsResizerDisabledHTMLElement;

        while (matchedElement && !matchedElement.tableColumnsResizerDisabled) {
            matchedElement = matchedElement.parentElement;
        }

        return !!matchedElement;
    }

    @Input()
    public tableBodyRulerDirective: ElementRulerDirective;

    @Input()
    public pointerEventsElementRef: ElementRef;

    @Input()
    public columnsResize$: Subject<TableColumnConfig[]>;

    @Input()
    public autoResizeColumns$: Observable<any>;

    public readonly pluginEnabledState$ = new BehaviorSubject<boolean>(true);

    public readonly destroy$ = destroy$();

    @ViewChild('splitterTemplate', {read: TemplateRef, static: true})
    public splitterTemplateRef: TemplateRef<any>;

    @ViewChild('splitterTemplate', {read: ViewContainerRef, static: true})
    public splitterViewContainerRef: ViewContainerRef;

    private columnSplitters: ColumnSplitter[] = [];

    private splitterViewRef: EmbeddedViewRef<any>;
    private splitterElement: HTMLElement;
    private splitterDisplayInfo: SplitterDisplayInfo;

    private columnDefs: TableColumn[];
    private columnsConfig: TableColumnConfig[];
    private tableBottom: number;
    private columnsMeta: ColumnMeta[] = [];

    private splitterMovingInfo: SplitterMovingInfo;

    private readonly widthUnitClassBySuffixMap: {[suffix: string]: Type<TableColumnWidthUnit>} = _.keyBy(
        [Px, Pr],
        widthUnitClass => widthUnitClass.unitNumberSuffix
    );

    private draftColumns: ColumnMeta[] = [];

    private readonly columnsAutoResizer = new ColumnsAutoResizer();

    private readonly updateColumnSizes$ = new Subject<void>();

    private readonly sizingMode$ = new BehaviorSubject<TableColumnsSizingMode>(null);

    private tableComponent: TableComponent = this.injector.get(TableComponent);

    private readonly pluginEnabled$ = this.pluginEnabledState$.pipe(
        filter(state => state)
    );
    private readonly pluginDisabled$ = this.pluginEnabledState$.pipe(
        filter(state => !state)
    );

    constructor(
        private elementRef: ElementRef,
        private renderer: Renderer2,
        private changeDetectorRef: ChangeDetectorRef,
        private ngZone: NgZone,
        private injector: Injector,

        @Inject(DOCUMENT)
        private document: Document
    ) {}

    public ngDoCheck(): void {
        if (this.pluginEnabledState$.value) {
            this.checkColumnsSizingModeChange();

            if (!this.autoResizeColumns$ && this.canAutoResize()) {
                this.updateColumnSizes$.next();
            }
        }
    }

    public ngAfterViewInit(): void {
        this.columnsAutoResizer.columnsMeta = this.columnsMeta;
        this.columnsAutoResizer.complexTableElementRef = this.pointerEventsElementRef;
        this.columnsAutoResizer.tableBodyRulerDirective = this.tableBodyRulerDirective;

        const externalAutoResizeColumns$ = (this.autoResizeColumns$ || EMPTY).pipe(
            filter(() => this.canAutoResize())
        );

        const updateColumnSizes$ = merge(
            this.updateColumnSizes$,
            externalAutoResizeColumns$
        ).pipe(
            audit(() => WIDGET_UPDATES_GATING$.pipe(
                takeUntil(this.pluginDisabled$.pipe(
                    filter(disabled => !!disabled)
                ))
            )),
            tap(() => this.updateColumnsSizes())
        );

        const updateOnPluginEnabled$ = this.pluginEnabled$.pipe(
            tap(() => this.ngDoCheck())
        );

        const listenToPointerEvents$ = this.listenToPointerEvents().pipe(
            takeUntil(this.pluginDisabled$),
            repeatWhen(disabled$ => disabled$.pipe(
                switchMapTo(this.pluginEnabled$.pipe(
                    take(1)
                ))
            ))
        );

        merge(
            this.createSplitterElement(),
            updateColumnSizes$,
            updateOnPluginEnabled$,
            this.listenToTableDimensions(),
            this.sizingMode$.pipe(
                switchMap(sizingMode =>
                    sizingMode === TableColumnsSizingMode.MANUAL
                        ? listenToPointerEvents$
                        : NEVER
                )
            )
        ).pipe(
            takeUntil(this.destroy$)
        ).subscribe();
    }

    public onColumnCellRemoved(columnConfigIndex: number, adjustedRowIndex: number): void {
        const columnMeta = this.columnsMeta[columnConfigIndex];

        if (columnMeta) {
            columnMeta.elements.splice(adjustedRowIndex, 1);
        }
    }

    public onColumnCellMoved(
        columnConfigIndex: number,
        adjustedPreviousRowIndex: number,
        adjustedCurrentRowIndex: number
    ): void {
        const columnMeta = this.columnsMeta[columnConfigIndex];

        if (columnMeta) {
            const element = columnMeta.elements.splice(adjustedPreviousRowIndex, 1)[0];

            columnMeta.elements.splice(adjustedCurrentRowIndex, 0, element);
        }
    }

    public onColumnCellRendered(
        viewRef: EmbeddedViewRef<TableColumnTemplateContext>,
        columnDirective: TableColumnDirective,
        columnConfigIndex: number,
        columnDefIndex: number,
        rowIndex: number
    ): void {
        const element = columnDirective.getElement(viewRef);
        let columnMeta = this.columnsMeta[columnConfigIndex];

        if (!columnMeta) {
            columnMeta = this.columnsMeta[columnConfigIndex] = this.createColumnMeta(
                viewRef,
                columnDirective,
                columnConfigIndex,
                columnDefIndex
            );

            this.draftColumns.push(columnMeta);
        } else {
            columnMeta.elements.splice(rowIndex, 0, element);
            columnMeta.draftElements.push(element);
        }

        this.renderer.setStyle(element, 'width', columnMeta.widthPx ? `${columnMeta.widthPx}px` : '');
    }

    public onColumnAdded(args: TablePluginColumnConfigAddedArgs): void {
        this.columnsMeta.splice(args.adjustedColumnConfigIndex, 0, null);
    }

    public onColumnRemoved(adjustedColumnConfigIndex: number): void {
        this.columnsMeta.splice(adjustedColumnConfigIndex, 1);
    }

    public onColumnConfigIdentityChanged(newColumnConfig: TableColumnConfig, columnConfigIndex: number): void {
        const columnMeta = this.columnsMeta[columnConfigIndex];

        if (columnMeta) {
            columnMeta.columnConfig = newColumnConfig;

            this.draftColumns.push(columnMeta);
        }
    }

    public onColumnConfigMoved(
        previousColumnConfigIndex: number,
        columnConfigIndex: number
    ): void {
        const columnMeta = this.columnsMeta[previousColumnConfigIndex];

        this.columnsMeta.splice(previousColumnConfigIndex, 1);
        this.columnsMeta.splice(columnConfigIndex, 0, columnMeta);
    }

    public finalizeColumnsRendering(): void {
        if (this.draftColumns.length) {
            this.applyColumnsWidth();

            this.draftColumns.length = 0;
        } else {
            this.columnsMeta.forEach(columnMeta => {
                this.applyColumnElementsWidth(columnMeta.draftElements, columnMeta.widthPx);

                columnMeta.draftElements.length = 0;
            });
        }
    }

    public setupColumns(
        {columnDefs, columnsConfig, columnsSizingSteps}: TablePluginColumnsSetupArgs
    ): void {
        this.columnsConfig = columnsConfig;
        this.columnDefs = columnDefs;
        this.columnsAutoResizer.columnsSizingSteps = columnsSizingSteps;
    }

    public produceColumnSplitters(): void {
        this.columnSplitters = this.columnsMeta.reduce(
            (columnSplitters: ColumnSplitter[], columnMeta, iColumn) => {
                if (iColumn) {
                    columnSplitters[iColumn - 1].rightColumnMeta = columnMeta;
                }

                if (iColumn < this.columnsMeta.length - 1) {
                    columnSplitters.push({
                        index: iColumn,
                        leftColumnMeta: columnMeta,
                        rightColumnMeta: null
                    });
                }

                return columnSplitters;
            },
            []
        );

        this.updateColumnSizes$.next();
    }

    /**
     * Updates real positions and sizes of all columns.
     * Note that it may require a little time between columns rendering and calling this method,
     * since the real dimensions of DOM elements need to be updated.
     * So use {@link this.updateColumnSizes$} to schedule the updating.
     */
    private updateColumnsRealDimensions(): void {
        this.applyColumnsWidth();
        this.updateSpecifiedColumnsRealDimensions(this.columnsMeta);
    }

    private updateSpecifiedColumnsRealDimensions(columnsMeta: ColumnMeta[]): void {
        const {left: offsetLeft, top: offsetTop} = (this.elementRef.nativeElement as HTMLElement)
            .getBoundingClientRect();

        columnsMeta.forEach(columnMeta => {
            const {left: absoluteLeft, right: absoluteRight, top: absoluteTop, width} = columnMeta.element
                .getBoundingClientRect();

            columnMeta.realDimensions = {
                top: absoluteTop - offsetTop,
                absoluteTop,
                left: absoluteLeft - offsetLeft,
                absoluteLeft,
                right: absoluteRight - offsetLeft,
                absoluteRight,
                height: this.tableBottom - absoluteTop,
                width
            };
        });
    }

    /**
     * Updates positions and sizes of all column splitters.
     * Requires {@link this.updateColumnsRealDimensions} to be called before.
     */
    private updateColumnSplittersDimensions(): void {
        this.applyColumnsWidth();

        this.columnSplitters.forEach(columnSplitter => {
            const {
                      right:         columnRight,
                      absoluteRight: columnAbsoluteRight,
                      top,
                      absoluteTop:   columnAbsoluteTop,
                      height
                  } = columnSplitter.leftColumnMeta.realDimensions;

            columnSplitter.top = top;
            columnSplitter.left = columnRight;
            columnSplitter.height = height;
            columnSplitter.areaTop = columnAbsoluteTop;
            columnSplitter.areaLeft = columnAbsoluteRight - SPLITTER_AREA_HALF_THICKNESS;
        });
    }

    private createColumnMeta(
        viewRef: EmbeddedViewRef<TableColumnTemplateContext>,
        columnDirective: TableColumnDirective,
        columnConfigIndex: number,
        columnDefIndex: number
    ): ColumnMeta {
        const columnConfig = this.columnsConfig[columnConfigIndex];
        const element = columnDirective.getElement(viewRef);
        const columnMeta: ColumnMeta = {
            viewRef,
            element,
            viewRefIndex:          columnConfigIndex,
            columnConfig,
            columnDef:             this.columnDefs[columnDefIndex],
            columnHeaderDirective: columnDirective,
            width:                 this.parseWidthUnit(columnConfig.width),
            widthPx:               null,
            realDimensions:        null,
            elements:              [element],
            draftElements:         [element]
        };

        this.columnsMeta.length = Math.max(this.columnsMeta.length, columnConfigIndex + 1);
        this.columnsMeta[columnConfigIndex] = columnMeta;

        return columnMeta;
    }

    private calcWidthsForUnit(
        unit: Type<TableColumnWidthUnit>,
        calcData: ColumnWidthCalculatorData
    ): ColumnWidthCalculatorData {
        const acceptable = calcData.columnsMeta
            .filter(columnMeta => columnMeta.width instanceof unit);
        const widthsPx = (unit as any).mapToPx(
            acceptable.map(cm => cm.width),
            calcData.availableTableWidth
        );
        const availableTableWidth = widthsPx.reduce(
            (result: number, widthPx: number) => result - widthPx,
            calcData.availableTableWidth
        );

        widthsPx.forEach((widthPx: number, index: number) => acceptable[index].widthPx = widthPx);

        return {
            ...calcData,
            availableTableWidth
        };
    }

    private applyColumnsWidth(): void {
        if (this.columnsMeta.length) {
            const [tableWidth] = this.tableBodyRulerDirective.getSize();

            // Hidden table measures 0: Px.toPx would clamp all widths to 0.
            if (!(tableWidth > 0)) {
                return;
            }

            [Px, Pr].reduce(
                (calcData, unit) => this.calcWidthsForUnit(unit, calcData),
                {
                    tableWidth,
                    availableTableWidth: tableWidth,
                    columnsMeta: this.columnsMeta
                } as ColumnWidthCalculatorData
            );

            this.columnsMeta.forEach(columnMeta => {
                this.applyColumnElementsWidth(columnMeta.elements, columnMeta.widthPx);
                columnMeta.draftElements.length = 0;
            });
        }
    }

    private applyColumnElementsWidth(elements: HTMLElement[], widthPx: number): void {
        const widthStyleValue = widthPx !== null ? `${widthPx}px` : '';

        elements.forEach(element => this.renderer.setStyle(element, 'width', widthStyleValue));
    }

    private parseWidthUnit(width: number | string): TableColumnWidthUnit {
        const DEFAULT_UNIT_SUFFIX = 'px';

        if (typeof width === 'number') {
            return Number.isFinite(width) && width > 0 ? new Px(width) : null;
        }

        if (typeof width === 'string') {
            // Suffix is optional: "150" means "150px"
            const match = width.match(/^(\d+(?:\.\d+)?)(.*)$/);

            if (match) {
                const value = parseFloat(match[1]);
                const suffix = match[2] || DEFAULT_UNIT_SUFFIX;
                const UnitClass = this.widthUnitClassBySuffixMap[suffix];

                // `> 0` preserves the old behaviour for '0px' (auto width).
                if (UnitClass && value > 0) {
                    return new UnitClass(value);
                }
            }
        }

    return null;
}
    private listenToPointerEvents(): Observable<any> {
        const LEFT_MOUSE_BUTTON = 0;

        const pointerEventsElement = this.pointerEventsElementRef.nativeElement;
        const getMouseEventPos = (event: MouseEvent): Point => [event.clientX, event.clientY];
        const getTouchEventPosition = (event: TouchEvent): Point => {
            const touch: Touch = event.changedTouches[0];

            return [touch.clientX, touch.clientY];
        };

        let lastPointerY: number;
        let lastEvent: Event;
        let dragStartPointerX: number;

        const storeLastPointerY = ([, y]: Point) => lastPointerY = y;
        const storeLastEvent = (event: Event) => lastEvent = event;
        const storeDragStartPointerX = ([x]: Point) => dragStartPointerX = x;

        const stopEvent = (event: Event) => {
            event.preventDefault();
            event.stopPropagation();
        };
        const stopLastEvent = () => stopEvent(lastEvent);

        const mouseMove$ =
                  fromEventOutsideAngularZone<MouseEvent>(this.ngZone, pointerEventsElement, 'mousemove').pipe(
                      tap<MouseEvent>(storeLastEvent),
                      map(getMouseEventPos),
                      tap(storeLastPointerY),
                      share()
                  );

        const pointerMove$ = merge(
            mouseMove$,
            fromEventOutsideAngularZone<TouchEvent>(this.ngZone, pointerEventsElement, 'touchmove').pipe(
                tap<TouchEvent>(storeLastEvent),
                map(getTouchEventPosition)
            )
        ).pipe(
            share()
        );

        const mouseLeave$ =
                  fromEventOutsideAngularZone<MouseEvent>(this.ngZone, pointerEventsElement, 'mouseleave').pipe(
                      tap(storeLastEvent),
                      share()
                  );

        const dragStart$ = merge(
            fromEventOutsideAngularZone<MouseEvent>(this.ngZone, pointerEventsElement, 'mousedown').pipe(
                filter(event => event.button === LEFT_MOUSE_BUTTON),
                tap<MouseEvent>(storeLastEvent),
                map(getMouseEventPos)
            ),
            fromEventOutsideAngularZone<TouchEvent>(this.ngZone, pointerEventsElement, 'touchstart').pipe(
                tap<TouchEvent>(storeLastEvent),
                map(getTouchEventPosition)
            )
        ).pipe(
            tap(storeLastPointerY),
            tap(storeDragStartPointerX),
            share()
        );
        const drop$ = merge(
            fromEvent<MouseEvent>(this.document, 'mouseup').pipe(
                filter(event => event.button === LEFT_MOUSE_BUTTON),
                tap(storeLastEvent)
            ),
            fromEvent<TouchEvent>(this.document, 'touchend').pipe(
                tap(storeLastEvent)
            )
        ).pipe(
            tap(() => this.applyResizing()),
            share()
        );
        const dragCancel$ = mouseLeave$.pipe(
            tap(() => this.updateSplitterX(0)),
            share()
        );
        const dragStop$ = merge(
            drop$,
            dragCancel$
        ).pipe(
            share()
        );

        const getSplitterByPointerPos = ([x, y]: Point): ColumnSplitter => {
            let matchedSplitter = this.columnSplitters.find(splitter =>
                splitter.areaTop <= y
                && splitter.areaLeft <= x
                && splitter.areaTop + splitter.height > y
                && splitter.areaLeft + SPLITTER_AREA_THICKNESS > x
            ) || null;

            if (matchedSplitter && TableColumnsResizerComponent.isElementDisablesResizer(lastEvent.target as Element)) {
                matchedSplitter = null;
            }

            return matchedSplitter;
        };
        const null$ = of(null);

        const splitterByMouseMoving$ = mouseMove$.pipe(
            filter(() => !this.splitterMovingInfo),
            map(getSplitterByPointerPos),
            distinctUntilChanged(),
            switchMap(splitter => splitter
                ? merge(
                    timer(SPLITTER_APPEARANCE_DELAY).pipe(
                        mapTo(splitter),
                        takeUntil(mouseLeave$)
                    ),
                    mouseLeave$.pipe(
                        mapTo(null),
                        take(1)
                    )
                )
                : null$
            )
        );

        const splitterByDragStart$ = dragStart$.pipe(
            switchMap(pointerPos => {
                const splitter = getSplitterByPointerPos(pointerPos);

                if (splitter) {
                    stopLastEvent(); // Prevents passing the event to other table plugins.
                }

                return splitter
                    ? dragStop$.pipe(
                        take(1),
                        mapTo(null),
                        startWith(splitter)
                    )
                    : null$;
            }),
            share()
        );

        const splitterVisualization$ = combineLatest(
            splitterByMouseMoving$.pipe(startWith(null)),
            splitterByDragStart$.pipe(startWith(null))
        ).pipe(
            map(([hoveringSplitter, draggingSplitter]) => hoveringSplitter || draggingSplitter),
            distinctUntilChanged(),
            switchMap(splitter => splitter
                ? pointerMove$.pipe(
                    map(([, pointerY]) => pointerY),
                    startWith(lastPointerY),
                    tap(pointerY => this.updateSplitterSign(pointerY)),
                    dependOn(this.showSplitter(splitter, lastPointerY))
                )
                : NEVER
            )
        );

        const splitterMoving$ = splitterByDragStart$.pipe(
            filter(splitter => !!splitter),
            tap(stopLastEvent),
            tap(splitter => this.setMovingSplitter(splitter)),
            switchMap(() =>
                pointerMove$.pipe(
                    map(([pointerX]) => this.clampSplitterShift(pointerX - dragStartPointerX)),
                    auditTime(0, animationFrameScheduler),
                    tap(shift => this.updateSplitterX(shift)),
                    takeUntil(dragStop$),
                    finalize(() => {
                        stopLastEvent();
                        this.setMovingSplitter(null);
                    })
                )
            )
        );

        return merge(
            splitterVisualization$,
            splitterMoving$
        );
    }

    private createSplitterElement(): Observable<any> {
        return new Observable(() => {
            this.splitterDisplayInfo = {};
            this.splitterViewRef = this.splitterTemplateRef.createEmbeddedView({
                $implicit: this.splitterDisplayInfo
            });
            this.splitterElement = this.splitterViewRef.rootNodes
                .find((element: HTMLElement) => !!element.tagName);

            return () => {
                this.splitterElement = null;
                this.splitterViewRef.destroy();
            };
        });
    }

    private listenToTableDimensions(): Observable<any> {
        return merge(
            this.tableBodyRulerDirective.dimensions$,
            defer(() => of(this.tableBodyRulerDirective.getDimensions()))
        ).pipe(
            tap(({pos, size}) => this.tableBottom = pos[Y] + size[Y] - 1),
            tap(() => this.updateColumnSizes$.next())
        );
    }

    private showSplitter(splitter: ColumnSplitter, pointerY: number): Observable<any> {
        return new Observable(() => {
            this.splitterDisplayInfo.splitter = splitter;
            this.splitterDisplayInfo.splitterX = splitter.left;
            this.splitterDisplayInfo.signY = pointerY - this.splitterDisplayInfo.splitter.areaTop;

            this.splitterViewRef.detectChanges();
            this.splitterViewContainerRef.insert(this.splitterViewRef);

            return () => {
                const splitterViewRefIndex: number = this.splitterViewContainerRef.indexOf(this.splitterViewRef);

                this.splitterViewContainerRef.detach(splitterViewRefIndex);
            };
        });
    }

    private updateSplitterSign(pointerY: number): void {
        this.splitterDisplayInfo.signY = pointerY - this.splitterDisplayInfo.splitter.areaTop;
        this.splitterViewRef.detectChanges();
    }

    private updateSplitterX(shift: number): void {
        if (!this.splitterMovingInfo) {
            return;
        }

        const splitter: ColumnSplitter = this.splitterDisplayInfo.splitter;
        const prevShift: number = this.splitterDisplayInfo.splitterX - splitter.left;

        this.splitterDisplayInfo.splitterX = splitter.left + shift;
        this.splitterMovingInfo.shift = shift;
        this.splitterViewRef.detectChanges();

        splitter.leftColumnMeta.widthPx += shift - prevShift;
        splitter.rightColumnMeta.widthPx -= shift - prevShift;

        this.applyColumnElementsWidth(splitter.leftColumnMeta.elements, splitter.leftColumnMeta.widthPx);
        this.applyColumnElementsWidth(splitter.rightColumnMeta.elements, splitter.rightColumnMeta.widthPx);
    }

    private setMovingSplitter(splitter: ColumnSplitter | null): void {
        this.splitterMovingInfo = splitter && {
            splitter,
            shift: 0,
            shiftingRange: this.getSplitterShiftingRange(splitter),
            isTooLargeShiftDelta: false
        };

        this.toggleClass(this.elementRef.nativeElement, RESIZING_CLASS, !!splitter);
    }

    private toggleClass(element: HTMLElement, className: string, state: boolean): void {
        const renderer = this.renderer;

        if (state) {
            renderer.addClass(element, className);
        } else {
            renderer.removeClass(element, className);
        }
    }

    private applyResizing(): void {
        if (!this.splitterMovingInfo?.shift) {
            return;
        }

        const shift: number = this.splitterMovingInfo.shift;

        if (shift) {
            const {leftColumnMeta, rightColumnMeta} = this.splitterMovingInfo.splitter;

            this.updateSpecifiedColumnsRealDimensions([leftColumnMeta, rightColumnMeta]);

            leftColumnMeta.width.adjustToPx(
                // Make opposite changes in order to compensate changes made by updateSplitterX.
                leftColumnMeta.widthPx - shift,
                leftColumnMeta.realDimensions.width
            );
            rightColumnMeta.width.adjustToPx(
                // Make opposite changes in order to compensate changes made by updateSplitterX.
                rightColumnMeta.widthPx + shift,
                rightColumnMeta.realDimensions.width
            );

            const {left: rightColumnLeft, absoluteLeft: rightColumnAbsoluteLeft} = rightColumnMeta.realDimensions;

            this.splitterMovingInfo.splitter.left = rightColumnLeft;
            this.splitterMovingInfo.splitter.areaLeft = rightColumnAbsoluteLeft - SPLITTER_AREA_HALF_THICKNESS;

            leftColumnMeta.columnConfig.width = leftColumnMeta.width.toString();
            rightColumnMeta.columnConfig.width = rightColumnMeta.width.toString();

            if (this.columnsResize$) {
                this.columnsResize$.next(this.columnsConfig); // implementedColumnsConfig
            }
        }
    }

    private getSplitterShiftingRange(splitter: ColumnSplitter): Point {
        const MIN_COLUMN_WIDTH_PX = 50;
        const leftShift = -splitter.leftColumnMeta.realDimensions.width + MIN_COLUMN_WIDTH_PX;
        const rightShift = splitter.rightColumnMeta.realDimensions.width - MIN_COLUMN_WIDTH_PX;

        return [
            Math.min(leftShift, 0),
            Math.max(rightShift, 0)
        ];
    }

    private clampSplitterShift(shift: number): number {
        const [lower, upper] = this.splitterMovingInfo.shiftingRange;
        const clampedShift = _.clamp(shift, lower, upper);
        const isTooLargeDelta = Math.abs(shift - clampedShift) > SPLITTER_TOO_LARGE_SHIFT_DELTA;

        if (isTooLargeDelta !== this.splitterMovingInfo.isTooLargeShiftDelta) {
            this.splitterMovingInfo.isTooLargeShiftDelta = isTooLargeDelta;
            this.toggleClass(this.elementRef.nativeElement, RESIZING_TOO_LARGE_SHIFT_DELTA_CLASS, isTooLargeDelta);
        }

        return clampedShift;
    }

    private checkColumnsSizingModeChange(): void {
        let newColumnsSizingMode: TableColumnsSizingMode = this.tableComponent.table.columnsSizingMode;

        newColumnsSizingMode = newColumnsSizingMode === undefined || newColumnsSizingMode === null
            ? DEFAULT_TABLE_COLUMN_SIZING_MODE
            : newColumnsSizingMode;

        if (newColumnsSizingMode !== this.sizingMode$.getValue()) {
            this.setColumnsSizingMode(newColumnsSizingMode);
        }
    }

    private setColumnsSizingMode(columnsSizingMode: TableColumnsSizingMode): void {
        if (columnsSizingMode === TableColumnsSizingMode.MANUAL) {
            this.translateColumnWidthsToPr();
        }

        this.sizingMode$.next(columnsSizingMode);
        this.updateColumnSizes$.next();
    }

    private updateColumnsSizes(): void {
        if (this.canAutoResize()) {
            this.columnsAutoResizer.applyAutoResizing();
        }

        this.updateColumnsRealDimensions();
        this.updateColumnSplittersDimensions();
    }

    private translateColumnWidthsToPr(): void {
        const [tableWidth] = this.tableBodyRulerDirective.getSize();
        const availableTableWidth = this.columnsMeta.reduce((currentAvailableTableWidth, columnMeta) => {
            if (!(columnMeta.width instanceof Px)) {
                currentAvailableTableWidth -= columnMeta.widthPx;
            }

            return currentAvailableTableWidth;
        }, tableWidth);

        const updatedColumnsConfig = this.columnsMeta
            .filter(columnMeta => columnMeta.width instanceof Px)
            .map(columnMeta => {
                const widthPr = availableTableWidth > 0
                    ? columnMeta.widthPx / availableTableWidth * 100
                    : 0;

                columnMeta.width = new Pr(widthPr);
                columnMeta.columnConfig.width = columnMeta.width.toString();

                return columnMeta.columnConfig;
        });

        if (this.columnsResize$ && updatedColumnsConfig.length) {
            this.columnsResize$.next(this.columnsConfig);
        }
    }

    private canAutoResize(): boolean {
        const columnsSizingSteps = this.columnsAutoResizer.columnsSizingSteps;

        return this.sizingMode$.getValue() === TableColumnsSizingMode.AUTO
            && !!(columnsSizingSteps && columnsSizingSteps.length);
    }

}
